import { h, clear } from './utils/dom.js';
import { createStore } from './state.js';
import { createRouter } from './router.js';
import { loadIndex, loadTranslations, loadChapter } from './api.js';
import {
	settingsStore,
	lastReadStore,
	bookmarksStore,
	highlightsStore,
	notesStore,
	readingPlanStore,
	chapterCache,
} from './storage.js';
import { parseReference, searchCached, deepSearch } from './search.js';
import * as tts from './tts.js';
import { READING_PLANS } from './reading-plans.js';
import { KEY_VERSES } from './key-verses.js';
import { renderNavigator } from './components/navigator.js';
import { renderReader } from './components/reader.js';
import { renderToolbar, FONT_SIZES } from './components/toolbar.js';
import { renderSearchPanel } from './components/search-panel.js';
import { renderSavedPanel } from './components/saved-panel.js';
import { renderComparePanel } from './components/compare-panel.js';
import { renderCompareView } from './components/compare-view.js';
import { renderPlanPanel } from './components/plan-panel.js';

function buildShell(root) {
	clear(root);

	const toolbar = h('div', { class: 'blb-toolbar' });
	const reader = h('div', { class: 'blb-reader' });
	const navBody = h('div', { class: 'blb-drawer-body' });
	const savedBody = h('div', { class: 'blb-drawer-body' });
	const searchBox = h('div', { class: 'blb-search-box' });

	const navDrawer = h('div', { class: 'blb-drawer' }, [
		h('div', { class: 'blb-drawer-panel' }, [
			h('div', { class: 'blb-drawer-header' }, [
				h('span', { class: 'blb-drawer-title' }, '📚 Browse'),
				h('button', { class: 'blb-close-btn', id: 'blb-nav-close' }, '✕'),
			]),
			navBody,
		]),
	]);

	const savedDrawer = h('div', { class: 'blb-drawer blb-drawer-right' }, [
		h('div', { class: 'blb-drawer-panel' }, [
			h('div', { class: 'blb-drawer-header' }, [
				h('span', { class: 'blb-drawer-title' }, '⭐ Saved'),
				h('button', { class: 'blb-close-btn', id: 'blb-saved-close' }, '✕'),
			]),
			savedBody,
		]),
	]);

	const compareBody = h('div', { class: 'blb-drawer-body' });
	const compareDrawer = h('div', { class: 'blb-drawer blb-drawer-right' }, [
		h('div', { class: 'blb-drawer-panel' }, [
			h('div', { class: 'blb-drawer-header' }, [
				h('span', { class: 'blb-drawer-title' }, '⇄ Compare Translations'),
				h('button', { class: 'blb-close-btn', id: 'blb-compare-close' }, '✕'),
			]),
			compareBody,
		]),
	]);

	const planBody = h('div', { class: 'blb-drawer-body' });
	const planDrawer = h('div', { class: 'blb-drawer blb-drawer-right' }, [
		h('div', { class: 'blb-drawer-panel' }, [
			h('div', { class: 'blb-drawer-header' }, [
				h('span', { class: 'blb-drawer-title' }, '📅 Reading Plans'),
				h('button', { class: 'blb-close-btn', id: 'blb-plan-close' }, '✕'),
			]),
			planBody,
		]),
	]);

	const searchOverlay = h('div', { class: 'blb-search-overlay' }, [searchBox]);

	const body = h('div', { class: 'blb-body' }, [reader, navDrawer, savedDrawer, compareDrawer, planDrawer, searchOverlay]);
	const shell = h('div', { class: 'blb-shell' }, [toolbar, body]);

	root.appendChild(shell);

	return {
		toolbar,
		reader,
		navDrawer,
		navBody,
		savedDrawer,
		savedBody,
		compareDrawer,
		compareBody,
		planDrawer,
		planBody,
		searchOverlay,
		searchBox,
	};
}

function mountApp(root) {
	const config = window.BLB_BIBLE_APP || {};
	const dataUrl = config.dataUrl || '';
	const i18n = Object.assign(
		{
			search: 'Search the Bible…',
			searchPlaceholder: 'Try "John 3:16" or a keyword',
			loading: 'Loading…',
			chapterMissing: 'This chapter is not included in the demo dataset yet.',
			noResults: 'No results found.',
			bookmarks: 'Bookmarks',
			highlights: 'Highlights',
			oldTestament: 'Old Testament',
			newTestament: 'New Testament',
		},
		config.i18n || {}
	);
	const globalFeatures = Object.assign(
		{
			search: true,
			bookmarks: true,
			highlights: true,
			offlineCache: true,
			compare: true,
			notes: true,
			readAloud: true,
			plans: true,
		},
		config.features || {}
	);
	// Per-shortcode-instance overrides always rendered by PHP; offlineCache has no
	// per-instance override so it stays governed by the global admin setting.
	const features = {
		search: root.dataset.featureSearch === undefined ? globalFeatures.search : root.dataset.featureSearch === '1',
		bookmarks: root.dataset.featureBookmarks === undefined ? globalFeatures.bookmarks : root.dataset.featureBookmarks === '1',
		highlights: root.dataset.featureHighlights === undefined ? globalFeatures.highlights : root.dataset.featureHighlights === '1',
		compare: root.dataset.featureCompare === undefined ? globalFeatures.compare : root.dataset.featureCompare === '1',
		notes: root.dataset.featureNotes === undefined ? globalFeatures.notes : root.dataset.featureNotes === '1',
		readAloud: root.dataset.featureReadAloud === undefined ? globalFeatures.readAloud : root.dataset.featureReadAloud === '1',
		plans: root.dataset.featurePlans === undefined ? globalFeatures.plans : root.dataset.featurePlans === '1',
		offlineCache: globalFeatures.offlineCache,
	};
	const compareMax = Math.max(2, Math.min(6, parseInt(config.compareMax, 10) || 3));

	const instanceKey = root.id || 'blb-app';
	const router = createRouter(instanceKey);

	const savedSettings = settingsStore.get({
		theme: root.dataset.theme || 'light',
		fontSize: root.dataset.defaultFontSize || 'medium',
		lineHeight: root.dataset.defaultLineHeight || 'normal',
		translation: root.dataset.defaultTranslation || 'kjv',
	});

	const fromHash = router.readHash();

	// Plain (non-namespaced) query-string deep link, e.g. from the category
	// search widget's Bible-verse results (?blb_book=john&blb_chapter=3&...),
	// which can't know this reader instance's randomly-generated hash key.
	const searchParams = new URLSearchParams(window.location.search);
	const fromQuery = searchParams.has('blb_book')
		? {
				book: searchParams.get('blb_book'),
				chapter: parseInt(searchParams.get('blb_chapter'), 10) || 1,
				translation: searchParams.get('blb_translation') || null,
				verse: parseInt(searchParams.get('blb_verse'), 10) || 0,
				verseEnd: parseInt(searchParams.get('blb_verse_end'), 10) || 0,
				highlight: searchParams.get('blb_highlight') || '',
			}
		: null;

	const fromLastRead = !fromHash && !fromQuery ? lastReadStore.get() : null;

	const initialBook = (fromHash && fromHash.book) || (fromQuery && fromQuery.book) || (fromLastRead && fromLastRead.book) || root.dataset.defaultBook || 'genesis';
	const initialChapter =
		(fromHash && fromHash.chapter) || (fromQuery && fromQuery.chapter) || (fromLastRead && fromLastRead.chapter) || parseInt(root.dataset.defaultChapter, 10) || 1;
	const initialTranslation = (fromHash && fromHash.translation) || (fromQuery && fromQuery.translation) || savedSettings.translation;
	const initialVerse = fromQuery ? fromQuery.verse : !fromHash ? parseInt(root.dataset.defaultVerse, 10) || 0 : 0;

	const store = createStore({
		index: null,
		translations: [],
		translation: initialTranslation,
		book: initialBook,
		chapter: initialChapter,
		chapterData: null,
		loading: true,
		selectedVerse: null,
		selectionEnd: null,
		shareMenuVerse: null,
		theme: savedSettings.theme,
		fontSize: savedSettings.fontSize,
		lineHeight: savedSettings.lineHeight,
		bookmarks: bookmarksStore.all(),
		highlights: highlightsStore.all(),
		notes: notesStore.all(),
		noteEditingVerse: null,
		plan: readingPlanStore.get(),
		speaking: { active: false, paused: false, verse: null },
		features,
		compareMax,
		i18n,
		drawer: null,
		translationMenuOpen: false,
		nav: { testament: 'OT', view: 'books', bookId: null },
		search: {
			open: false,
			query: '',
			results: [],
			reference: null,
			deepRunning: false,
			deepProgress: { scanned: 0, total: 0 },
			focusPending: false,
		},
		compare: {
			mode: false,
			loading: false,
			selected: initialTranslation ? [initialTranslation] : [],
			data: {},
		},
	});

	let deepCancelFn = null;
	const els = buildShell(root);

	root.setAttribute('data-theme', store.get().theme);
	root.setAttribute('data-font-size', store.get().fontSize);
	root.setAttribute('data-line-height', store.get().lineHeight);

	async function openChapter(book, chapter, opts) {
		const preserveSelection = !!(opts && opts.preserveSelection);
		tts.stop();
		const prev = store.get();
		store.set({
			loading: true,
			selectedVerse: preserveSelection ? prev.selectedVerse : null,
			selectionEnd: preserveSelection ? prev.selectionEnd : null,
			shareMenuVerse: null,
			noteEditingVerse: null,
			speaking: { active: false, paused: false, verse: null },
		});
		const translation = store.get().translation;
		const result = await loadChapter(dataUrl, translation, book, chapter, store.get().features.offlineCache);
		store.set({
			book,
			chapter,
			chapterData: result.data,
			loading: false,
		});
		lastReadStore.set(book, chapter);
		router.writeHash(book, chapter, translation);

		if (store.get().compare.mode) {
			await loadCompareData(book, chapter);
		}
	}

	async function loadCompareData(book, chapter) {
		const s = store.get();
		store.set((state) => ({ compare: Object.assign({}, state.compare, { loading: true }) }));

		const entries = await Promise.all(
			s.compare.selected.map(async (translationId) => {
				const result = await loadChapter(dataUrl, translationId, book, chapter, s.features.offlineCache);
				return [translationId, result.data];
			})
		);

		const data = Object.fromEntries(entries);
		store.set((state) => ({ compare: Object.assign({}, state.compare, { data, loading: false }) }));
	}

	const actions = {
		openDrawer(name) {
			store.set({ drawer: name });
		},
		closeDrawer() {
			store.set({ drawer: null });
		},
		setNavTestament(t) {
			store.set({ nav: { testament: t, view: 'books', bookId: null } });
		},
		setNavView(view) {
			store.set((s) => ({ nav: Object.assign({}, s.nav, { view }) }));
		},
		openBookChapters(bookId) {
			store.set((s) => ({ nav: Object.assign({}, s.nav, { view: 'chapters', bookId }) }));
		},
		selectChapter(book, chapter) {
			store.set({ drawer: null });
			openChapter(book, chapter);
		},
		nextChapter() {
			const s = store.get();
			const meta = s.index.books.find((b) => b.id === s.book);
			if (meta && s.chapter < meta.chapters) openChapter(s.book, s.chapter + 1);
		},
		prevChapter() {
			const s = store.get();
			if (s.chapter > 1) openChapter(s.book, s.chapter - 1);
		},
		selectVerse(verseNum) {
			store.set((s) => {
				if (s.selectedVerse === null) {
					return { selectedVerse: verseNum, selectionEnd: verseNum, shareMenuVerse: null, noteEditingVerse: null };
				}
				if (s.selectedVerse === s.selectionEnd && s.selectedVerse === verseNum) {
					// Clicking the sole selected verse again deselects it.
					return { selectedVerse: null, selectionEnd: null, shareMenuVerse: null, noteEditingVerse: null };
				}
				// A selection is already active: extend/redefine the range end.
				return { selectionEnd: verseNum, shareMenuVerse: null, noteEditingVerse: null };
			});
		},
		clearSelection() {
			store.set({ selectedVerse: null, selectionEnd: null, shareMenuVerse: null, noteEditingVerse: null });
		},
		selectionRange() {
			const s = store.get();
			if (s.selectedVerse === null || s.selectionEnd === null) return null;
			return [Math.min(s.selectedVerse, s.selectionEnd), Math.max(s.selectedVerse, s.selectionEnd)];
		},
		isBookmarked(verse) {
			const s = store.get();
			return bookmarksStore.has(s.book, s.chapter, verse);
		},
		toggleBookmark(verse, text) {
			const s = store.get();
			bookmarksStore.toggle(s.book, s.chapter, verse, text);
			store.set({ bookmarks: bookmarksStore.all() });
		},
		removeBookmark(book, chapter, verse) {
			bookmarksStore.remove(book, chapter, verse);
			store.set({ bookmarks: bookmarksStore.all() });
		},
		getHighlight(verse) {
			const s = store.get();
			return highlightsStore.get(s.book, s.chapter, verse);
		},
		setHighlight(verse, color, book, chapter) {
			const s = store.get();
			highlightsStore.set(book || s.book, chapter || s.chapter, verse, color);
			store.set({ highlights: highlightsStore.all() });
		},
		/**
		 * Apply one highlight color to every verse in the current selection
		 * range (a single-verse selection is just a range of one).
		 */
		setHighlightRange(color) {
			const s = store.get();
			if (s.selectedVerse === null || s.selectionEnd === null || !s.chapterData) return;
			const start = Math.min(s.selectedVerse, s.selectionEnd);
			const end = Math.max(s.selectedVerse, s.selectionEnd);
			for (let v = start; v <= end; v++) {
				highlightsStore.set(s.book, s.chapter, v, color);
			}
			store.set({ highlights: highlightsStore.all() });
		},
		toggleShareMenu(verse) {
			store.set((s) => ({ shareMenuVerse: s.shareMenuVerse === verse ? null : verse }));
		},
		closeShareMenu() {
			store.set({ shareMenuVerse: null });
		},
		adjustFontSize(dir) {
			const s = store.get();
			const idx = FONT_SIZES.indexOf(s.fontSize);
			const next = FONT_SIZES[Math.min(FONT_SIZES.length - 1, Math.max(0, idx + dir))];
			store.set({ fontSize: next });
			settingsStore.set({ theme: store.get().theme, fontSize: next, lineHeight: store.get().lineHeight, translation: store.get().translation });
			root.setAttribute('data-font-size', next);
		},
		cycleLineHeight() {
			const order = ['compact', 'normal', 'relaxed'];
			const s = store.get();
			const next = order[(order.indexOf(s.lineHeight) + 1) % order.length];
			store.set({ lineHeight: next });
			settingsStore.set({ theme: store.get().theme, fontSize: store.get().fontSize, lineHeight: next, translation: store.get().translation });
			root.setAttribute('data-line-height', next);
		},
		cycleTheme() {
			const order = ['light', 'dark', 'sepia'];
			const s = store.get();
			const next = order[(order.indexOf(s.theme) + 1) % order.length];
			store.set({ theme: next });
			settingsStore.set({ theme: next, fontSize: store.get().fontSize, lineHeight: store.get().lineHeight, translation: store.get().translation });
			root.setAttribute('data-theme', next);
		},
		setTranslation(translation) {
			const s = store.get();
			if (translation === s.translation) return;
			store.set({ translation, search: Object.assign({}, s.search, { query: '', results: [], reference: null }) });
			settingsStore.set({ theme: s.theme, fontSize: s.fontSize, lineHeight: s.lineHeight, translation });
			openChapter(s.book, s.chapter, { preserveSelection: true });
		},
		toggleTranslationMenu() {
			store.set((s) => ({ translationMenuOpen: !s.translationMenuOpen }));
		},
		closeTranslationMenu() {
			store.set({ translationMenuOpen: false });
		},
		toggleCompareTranslation(translationId) {
			const s = store.get();
			const isSelected = s.compare.selected.includes(translationId);

			let selected;
			if (isSelected) {
				selected = s.compare.selected.filter((id) => id !== translationId);
			} else {
				if (s.compare.selected.length >= s.compareMax) return;
				selected = [...s.compare.selected, translationId];
			}

			store.set((state) => ({ compare: Object.assign({}, state.compare, { selected }) }));
		},
		async viewCompare() {
			const s = store.get();
			if (s.compare.selected.length < 2) return;

			store.set((state) => ({ drawer: null, compare: Object.assign({}, state.compare, { mode: true }) }));
			await loadCompareData(s.book, s.chapter);
		},
		exitCompare() {
			store.set((state) => ({ compare: Object.assign({}, state.compare, { mode: false }) }));
		},
		openSearch() {
			store.set((s) => ({ search: Object.assign({}, s.search, { open: true, focusPending: true }) }));
		},
		closeSearch() {
			if (deepCancelFn) {
				deepCancelFn();
				deepCancelFn = null;
			}
			store.set((s) => ({
				search: Object.assign({}, s.search, { open: false, deepRunning: false }),
			}));
		},
		clearSearchFocusFlag() {
			store.set((s) => ({ search: Object.assign({}, s.search, { focusPending: false }) }));
		},
		async runSearch(query) {
			if (deepCancelFn) {
				deepCancelFn();
				deepCancelFn = null;
			}

			const s = store.get();
			const reference = parseReference(query, s.index);
			const results = query.trim() ? await searchCached(query, s.translation) : [];

			store.set((state) => ({
				search: Object.assign({}, state.search, {
					query,
					reference,
					results,
					deepRunning: false,
					deepProgress: { scanned: 0, total: 0 },
				}),
			}));
		},
		startDeepSearch() {
			const s = store.get();
			const query = s.search.query;
			if (!query.trim()) return;

			const seen = new Set();
			store.set((state) => ({
				search: Object.assign({}, state.search, { deepRunning: true, deepProgress: { scanned: 0, total: 0 } }),
			}));

			// Cached chapters scan near-instantly, so onMatch/onProgress can
			// each fire dozens of times per second. Committing every single
			// one straight to the store re-renders (and rebuilds) the whole
			// search panel that often, including its Close button — fast
			// enough that a click's mousedown and mouseup can land on two
			// different DOM instances of "the same" button, so the browser
			// never fires the click at all. Buffer updates and flush at most
			// a few times a second instead.
			let pendingMatches = [];
			let pendingProgress = null;
			let flushTimer = null;

			function flush() {
				flushTimer = null;
				if (!pendingMatches.length && !pendingProgress) return;
				const matches = pendingMatches;
				const progress = pendingProgress;
				pendingMatches = [];
				pendingProgress = null;
				store.set((state) => ({
					search: Object.assign({}, state.search, {
						results: matches.length ? [...state.search.results, ...matches] : state.search.results,
						deepProgress: progress || state.search.deepProgress,
					}),
				}));
			}

			function scheduleFlush() {
				if (!flushTimer) {
					flushTimer = window.setTimeout(flush, 150);
				}
			}

			const cancel = deepSearch(dataUrl, s.index, s.translation, query, s.features.offlineCache, {
				onMatch(match) {
					const key = `${match.book}-${match.chapter}-${match.verse}`;
					if (seen.has(key)) return;
					seen.add(key);
					pendingMatches.push(match);
					scheduleFlush();
				},
				onProgress(scanned, total) {
					pendingProgress = { scanned, total };
					scheduleFlush();
				},
				onDone() {
					flush();
					store.set((state) => ({ search: Object.assign({}, state.search, { deepRunning: false }) }));
					deepCancelFn = null;
				},
			});

			deepCancelFn = () => {
				cancel();
				if (flushTimer) {
					window.clearTimeout(flushTimer);
					flushTimer = null;
				}
			};
		},
		stopDeepSearch() {
			if (deepCancelFn) {
				deepCancelFn();
				deepCancelFn = null;
			}
			store.set((s) => ({ search: Object.assign({}, s.search, { deepRunning: false }) }));
		},
		goToReference(ref) {
			store.set((s) => ({
				drawer: null,
				search: Object.assign({}, s.search, { open: false }),
			}));
			if (deepCancelFn) {
				deepCancelFn();
				deepCancelFn = null;
			}
			openChapter(ref.book, ref.chapter || 1).then(() => {
				let verse = ref.verse;
				if ('random' === verse) {
					const verses = store.get().chapterData ? store.get().chapterData.verses : [];
					verse = verses.length ? verses[Math.floor(Math.random() * verses.length)].verse : null;
				}
				if (verse) {
					store.set({ selectedVerse: verse, selectionEnd: verse });
					requestAnimationFrame(() => {
						const el = els.reader.querySelector('.blb-verse-selected');
						if (el) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
					});
				}
			});
		},
		randomChapter() {
			const s = store.get();
			if (!s.index) return;

			const all = [];
			s.index.books.forEach((b) => {
				for (let c = 1; c <= b.chapters; c++) {
					all.push({ book: b.id, chapter: c });
				}
			});
			if (!all.length) return;

			const pick = all[Math.floor(Math.random() * all.length)];
			actions.goToReference({ book: pick.book, chapter: pick.chapter, verse: 'random' });
		},
		randomKeyVerse() {
			const pick = KEY_VERSES[Math.floor(Math.random() * KEY_VERSES.length)];
			actions.goToReference({ book: pick.book, chapter: pick.chapter, verse: pick.verse });
		},

		// ---- Notes ----
		getNote(verse) {
			const s = store.get();
			const note = notesStore.get(s.book, s.chapter, verse);
			return note ? note.text : null;
		},
		toggleNoteEditor(verse) {
			store.set((s) => ({ noteEditingVerse: s.noteEditingVerse === verse ? null : verse }));
		},
		closeNoteEditor() {
			store.set({ noteEditingVerse: null });
		},
		saveNote(verse, text) {
			const s = store.get();
			const verseObj = s.chapterData && s.chapterData.verses.find((v) => v.verse === verse);
			notesStore.set(s.book, s.chapter, verse, text, verseObj ? verseObj.text : '');
			store.set({ notes: notesStore.all(), noteEditingVerse: null });
		},
		deleteNote(verse, book, chapter) {
			const s = store.get();
			notesStore.remove(book || s.book, chapter || s.chapter, verse);
			store.set({ notes: notesStore.all(), noteEditingVerse: null });
		},

		// ---- Read aloud (Web Speech API — no external service) ----
		ttsSupported() {
			return tts.isSupported();
		},
		startReadAloud() {
			const s = store.get();
			if (!s.chapterData) return;
			store.set({ speaking: { active: true, paused: false, verse: null } });
			tts.speakChapter(s.chapterData.verses, {
				onVerseStart: (verseNum) => {
					store.set((state) => ({ speaking: Object.assign({}, state.speaking, { verse: verseNum }) }));
				},
				onEnd: () => {
					store.set({ speaking: { active: false, paused: false, verse: null } });
				},
			});
		},
		speakVerse(verse, text) {
			store.set({ speaking: { active: true, paused: false, verse } });
			tts.speakChapter([{ verse, text }], {
				onVerseStart: () => {},
				onEnd: () => {
					store.set({ speaking: { active: false, paused: false, verse: null } });
				},
			});
		},
		speakRange() {
			const s = store.get();
			if (s.selectedVerse === null || s.selectionEnd === null || !s.chapterData) return;
			const start = Math.min(s.selectedVerse, s.selectionEnd);
			const end = Math.max(s.selectedVerse, s.selectionEnd);
			const verses = s.chapterData.verses.filter((v) => v.verse >= start && v.verse <= end);
			if (!verses.length) return;

			store.set({ speaking: { active: true, paused: false, verse: verses[0].verse } });
			tts.speakChapter(verses, {
				onVerseStart: (verseNum) => {
					store.set((state) => ({ speaking: Object.assign({}, state.speaking, { verse: verseNum }) }));
				},
				onEnd: () => {
					store.set({ speaking: { active: false, paused: false, verse: null } });
				},
			});
		},
		togglePauseReadAloud() {
			const s = store.get();
			if (s.speaking.paused) {
				tts.resume();
				store.set((state) => ({ speaking: Object.assign({}, state.speaking, { paused: false }) }));
			} else {
				tts.pause();
				store.set((state) => ({ speaking: Object.assign({}, state.speaking, { paused: true }) }));
			}
		},
		stopReadAloud() {
			tts.stop();
			store.set({ speaking: { active: false, paused: false, verse: null } });
		},

		// ---- Reading plans (schedules computed client-side, no external data) ----
		startPlan(planId) {
			const def = READING_PLANS.find((p) => p.id === planId);
			if (!def) return;
			store.set({ plan: readingPlanStore.start(planId, def.days) });
		},
		stopPlan() {
			readingPlanStore.stop();
			store.set({ plan: null });
		},
		toggleDayComplete(day) {
			const s = store.get();
			const isDone = s.plan && s.plan.completedDays.includes(day);
			const updated = isDone ? readingPlanStore.unmarkDayComplete(day) : readingPlanStore.markDayComplete(day);
			store.set({ plan: updated });
		},
	};

	els.navDrawer.addEventListener('click', (e) => {
		if (e.target === els.navDrawer) actions.closeDrawer();
	});
	els.savedDrawer.addEventListener('click', (e) => {
		if (e.target === els.savedDrawer) actions.closeDrawer();
	});
	els.compareDrawer.addEventListener('click', (e) => {
		if (e.target === els.compareDrawer) actions.closeDrawer();
	});
	els.planDrawer.addEventListener('click', (e) => {
		if (e.target === els.planDrawer) actions.closeDrawer();
	});
	els.searchOverlay.addEventListener('click', (e) => {
		if (e.target === els.searchOverlay) actions.closeSearch();
	});
	root.addEventListener('click', (e) => {
		if (
			e.target.id === 'blb-nav-close' ||
			e.target.id === 'blb-saved-close' ||
			e.target.id === 'blb-compare-close' ||
			e.target.id === 'blb-plan-close'
		) {
			actions.closeDrawer();
		}
	});
	document.addEventListener('click', (e) => {
		if (store.get().translationMenuOpen && !e.target.closest('.blb-translation-wrap')) {
			actions.closeTranslationMenu();
		}
	});
	root.addEventListener('keydown', (e) => {
		if (e.key === 'Escape') {
			actions.closeDrawer();
			actions.closeSearch();
			actions.closeTranslationMenu();
			actions.closeNoteEditor();
			actions.closeShareMenu();
		}
	});

	function render(state) {
		renderToolbar(els.toolbar, state, actions);

		if (state.compare.mode) {
			renderCompareView(els.reader, state, actions);
		} else {
			renderReader(els.reader, state, actions);
		}

		els.navDrawer.classList.toggle('blb-open', state.drawer === 'navigator');
		if (state.drawer === 'navigator') renderNavigator(els.navBody, state, actions);

		els.savedDrawer.classList.toggle('blb-open', state.drawer === 'saved');
		if (state.drawer === 'saved') renderSavedPanel(els.savedBody, state, actions);

		els.compareDrawer.classList.toggle('blb-open', state.drawer === 'compare');
		if (state.drawer === 'compare') renderComparePanel(els.compareBody, state, actions);

		els.planDrawer.classList.toggle('blb-open', state.drawer === 'plan');
		if (state.drawer === 'plan') renderPlanPanel(els.planBody, state, actions);

		renderSearchPanel(els.searchOverlay, els.searchBox, state, actions);
	}

	store.subscribe(render);
	render(store.get());

	chapterCache.ensureVersion(config.dataVersion || 1).then(async () => {
		const [index, translationRegistry] = await Promise.all([loadIndex(dataUrl), loadTranslations(dataUrl)]);
		if (!index) {
			store.set({ loading: false });
			return;
		}
		const translations = (translationRegistry && translationRegistry.translations) || [];
		const hasSavedTranslation = translations.some((t) => t.id === store.get().translation);

		store.set({
			index,
			translations,
			translation: hasSavedTranslation ? store.get().translation : translationRegistry.default || 'kjv',
			nav: { testament: 'OT', view: 'books', bookId: null },
		});
		await openChapter(store.get().book, store.get().chapter);

		if (initialVerse > 0) {
			const end = fromQuery && fromQuery.verseEnd > initialVerse ? fromQuery.verseEnd : initialVerse;

			// A scripture-reference link from a post asks for its verses to be
			// highlighted, not just selected, so the passage stays marked.
			if (fromQuery && ['yellow', 'green', 'blue', 'purple', 'red', 'amber'].includes(fromQuery.highlight)) {
				const s = store.get();
				for (let v = initialVerse; v <= end; v++) {
					highlightsStore.set(s.book, s.chapter, v, fromQuery.highlight);
				}
				store.set({ highlights: highlightsStore.all() });
			}

			store.set({ selectedVerse: initialVerse, selectionEnd: end });
			requestAnimationFrame(() => {
				const el = els.reader.querySelector('.blb-verse-selected');
				if (el) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
			});
		}
	});
}

function boot() {
	const nodes = document.querySelectorAll('.blb-bible-app');
	nodes.forEach((node) => {
		if (node.dataset.blbMounted) return;
		node.dataset.blbMounted = '1';
		mountApp(node);
	});
}

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', boot);
} else {
	boot();
}
